"use client";

import { useRef, useState } from "react";
import { Download } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { CaseManagementService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "@/components/SpotToast";
import { Button } from "@/components/shadcn/button";
import { SIDENAV_GROUP_ACTION_CLASS } from "@/components/Spotflow/SideNav/sidenavShared";
import { DATA_KEYS } from "@/utils/useRemoteData";
import {
  ImportOptionsDialog,
  ImportOptions,
} from "@/components/Dialogs/ImportOptionsDialog";

export const ImportInboxButton = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const router = useRouter();
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const [pendingImport, setPendingImport] = useState<{
    file: File;
    content: string;
  } | null>(null);

  const importMutation = useMutation({
    mutationFn: async ({
      file,
      options,
    }: {
      file: File;
      options: ImportOptions;
    }) => {
      const fileContent = await file.text();
      const query: Record<string, string | boolean> = { mode: options.mode };
      if (options.activate) query.activate = true;
      if (options.on_missing_dependency !== "fail")
        query.on_missing_dependency = options.on_missing_dependency;

      return CaseManagementService.importInbox({
        path: { group_id: spotGroup.id },
        body: { definition: fileContent },
        query: query as any,
      });
    },
    onMutate: () => {
      const toastId = SpotToast.loading("Importing inbox...");
      return { toastId };
    },
    onSuccess: (response: any, _variables, context) => {
      SpotToast.success(
        "Inbox imported successfully",
        undefined,
        undefined,
        context?.toastId
      );
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INBOXES] });
      const inboxItem = response.data?.find(
        (item: any) => item.type === "inbox"
      );
      if (inboxItem) {
        router.push(`/inbox/${inboxItem.reference}`);
      }
    },
    onError: (error: any, _variables, context) => {
      const errorMessage =
        error?.response?.data?.detail ||
        "Failed to import inbox. Please check the file and try again.";
      SpotToast.error(errorMessage, undefined, undefined, context?.toastId);
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".nx")) {
      SpotToast.error("Please select a .nx file");
      return;
    }
    const content = await file.text();
    setPendingImport({ file, content });
    e.target.value = "";
  };

  const handleConfirm = (options: ImportOptions) => {
    if (!pendingImport) return;
    importMutation.mutate({ file: pendingImport.file, options });
    setPendingImport(null);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".nx"
        className="hidden"
        onChange={handleFileChange}
      />
      <Button
        variant="ghost"
        size="icon"
        aria-label="Import inbox"
        className={SIDENAV_GROUP_ACTION_CLASS}
        onClick={() => inputRef.current?.click()}
        disabled={importMutation.isPending}
      >
        <Download className="size-4" strokeWidth={2} />
      </Button>
      <ImportOptionsDialog
        isOpen={pendingImport !== null}
        onOpenChange={(open) => !open && setPendingImport(null)}
        fileContent={pendingImport?.content ?? ""}
        fileName={pendingImport?.file.name ?? ""}
        isImporting={importMutation.isPending}
        onConfirm={handleConfirm}
      />
    </>
  );
};
